import { zodResolver } from '@hookform/resolvers/zod';
import { G } from '@mobily/ts-belt';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeftRounded';
import { IconButton, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  PostFirstConnectionRequest,
  PostForgotPasswordRequest,
  ZodPostForgotPasswordRequest,
} from '@Common/types/bo_external/auth/request_email';

import { TypographyTranslated } from '@sh4res/bo-common/components';
import { PageTitle } from '@sh4res/bo-common/components/PageTitle';

import { AxaTypography } from '@Components/AxaTypography';
import { LoginPageCTA } from '@Components/LoginPageCTA';
import { VerifyEmailPageSuccess } from '@Components/VerifyEmailPageSuccess';
import { TranslationKeys } from '@I18n/types';
import { authClient } from '@Utils/authClient';

const ForgotPasswordPageFormWrapper = styled('form')`
  text-align: center;
  display: flex;
  flex-direction: column;
  min-width: 290px;
`;

type Page = 'form' | 'success';
type FormData = PostFirstConnectionRequest | PostForgotPasswordRequest;

export function ForgotPasswordPageForm() {
  const translate = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();
  const testId = 'BoExternalForgotPasswordPageForm';

  const onGoBack = () => navigate(-1);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
  } = useForm<FormData>({
    mode: 'onBlur',
    resolver: zodResolver(ZodPostForgotPasswordRequest),
  });

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<Page>('form');

  const onSubmit = async (formData: FormData) => {
    setLoading(true);

    await authClient.requestPasswordReset(
      {
        email: formData.email,
        redirectTo: `${window.location.origin}/#/auth-callback?type=reset-password`,
      },
      {
        onResponse() {
          setLoading(false);
        },
        onSuccess() {
          setPage('success');
        },
        onError({ error }) {
          // oxlint-disable-next-line no-console
          // eslint-disable-next-line no-console
          console.error(error);
          notify('login_page.messages.forgot_password.failed', { type: 'error' });
        },
      },
    );
  };

  const rawHeaderText = 'login_page.messages.forgot_password.header';

  return page === 'success' ? (
    <div style={{ maxWidth: 320 }}>
      <VerifyEmailPageSuccess
        header="login_page.messages.forgot_password_success.header"
        subheader="login_page.messages.forgot_password_success.subheader"
        subheaderVariables={{ email: getValues().email }}
      />
    </div>
  ) : (
    <>
      <PageTitle title={translate(rawHeaderText)} />
      <IconButton
        data-testid={`${testId}_BackButton`}
        onClick={onGoBack}
        aria-label={translate('contact_support_page.accessibility.back')}
        sx={{
          color: 'black',
          backgroundColor: 'transparent !important;',
          position: 'absolute',
          top: 12,
          left: 12,
        }}>
        <ChevronLeftIcon sx={{ fontSize: '23px' }} />
      </IconButton>
      <ForgotPasswordPageFormWrapper onSubmit={handleSubmit(onSubmit)}>
        <TypographyTranslated
          label={rawHeaderText}
          variant="h5"
          component={AxaTypography}
          align="left"
          sx={{ mt: '40px' }}
        />
        <TypographyTranslated
          label={'login_page.messages.forgot_password.subheader'}
          variant="body2"
          align="left"
          sx={{ mb: '16px', mt: 0, maxWidth: '320px' }}
        />
        <TextField
          data-testid={`${testId}_EmailInput`}
          label={translate('login_page.messages.form.email_placeholder')}
          variant="outlined"
          sx={{
            borderRadius: '16px',
            mb: '24px',
            width: '100%',
          }}
          defaultValue=""
          placeholder="abc@def.com"
          InputLabelProps={{ shrink: true }}
          error={!!errors.email}
          helperText={
            G.isNotNullable(errors.email?.message)
              ? translate(errors.email.message as TranslationKeys)
              : undefined
          }
          {...register('email')}
        />

        <Stack alignItems="end">
          <LoginPageCTA
            data-testid={`${testId}_SubmitButton`}
            type="submit"
            variant="contained"
            disabled={!isValid || loading}
            sx={{ width: 'max-content' }}>
            <TypographyTranslated
              component={AxaTypography}
              label="login_page.messages.forgot_password.cta_text"
            />
          </LoginPageCTA>
        </Stack>
      </ForgotPasswordPageFormWrapper>
    </>
  );
}
